/**
 * Formatação de datas — pt-BR + America/Sao_Paulo.
 *
 * O backend armazena/retorna datetimes em UTC. O browser convertendo com
 * toLocaleString() sem locale usa o fuso do sistema do usuário, o que é
 * imprevisível (US, macOS default, etc). Aqui forçamos pt-BR + Brasília.
 */

const DATE_TIME_OPTS: Intl.DateTimeFormatOptions = {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
};

const DATE_ONLY_OPTS: Intl.DateTimeFormatOptions = {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
};

const TIME_ONLY_OPTS: Intl.DateTimeFormatOptions = {
    timeZone: 'America/Sao_Paulo',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
};

/**
 * Parse robusto de datetime vindo do backend.
 *
 * O backend Pydantic serializa datetime como ISO 8601 SEM timezone
 * (ex: "2026-07-08T23:00:00"). Sem 'Z' ou offset, o `new Date(str)` do JS
 * interpreta como HORÁRIO LOCAL do browser, o que gera um bug: se o browser
 * está em BRT (-3), ele acha que a string é BRT e converte pra UTC somando 3h.
 *
 * Aqui garantimos interpretação UTC quando falta timezone.
 */
export function parseBackendDate(input: string | Date | null | undefined): Date | null {
    if (!input) return null;
    if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
    // Se já tem 'Z' ou offset (+/-HH:MM), o Date interpreta certo
    const hasTz = /Z$|[+-]\d{2}:?\d{2}$/.test(input);
    const normalized = hasTz ? input : input + 'Z';
    const d = new Date(normalized);
    return isNaN(d.getTime()) ? null : d;
}

function _toDate(input: string | Date | null | undefined): Date | null {
    return parseBackendDate(input);
}

/** "01/07/2026 14:30" */
export function fmtDateTime(input: string | Date | null | undefined): string {
    const d = _toDate(input);
    if (!d) return '—';
    // Placeholder do banco pra "sem horário" — 9999-12-31
    if (d.getUTCFullYear() >= 9999) return 'sem horário';
    return new Intl.DateTimeFormat('pt-BR', DATE_TIME_OPTS).format(d);
}

/** "01/07/2026" */
export function fmtDate(input: string | Date | null | undefined): string {
    const d = _toDate(input);
    if (!d) return '—';
    if (d.getUTCFullYear() >= 9999) return 'sem horário';
    return new Intl.DateTimeFormat('pt-BR', DATE_ONLY_OPTS).format(d);
}

/** "14:30" */
export function fmtTime(input: string | Date | null | undefined): string {
    const d = _toDate(input);
    if (!d) return '—';
    if (d.getUTCFullYear() >= 9999) return '—';
    return new Intl.DateTimeFormat('pt-BR', TIME_ONLY_OPTS).format(d);
}

/**
 * "há 3 min", "há 2h", "há 5 dias", "amanhã 14:30", etc.
 * Relativo ao "agora".
 */
export function fmtRelative(input: string | Date | null | undefined): string {
    const d = _toDate(input);
    if (!d) return '—';
    if (d.getUTCFullYear() >= 9999) return 'sem horário';
    const now = new Date();
    const diffMs = d.getTime() - now.getTime();
    const absMin = Math.round(Math.abs(diffMs) / 60000);
    const past = diffMs < 0;

    if (absMin < 1) return past ? 'agora mesmo' : 'em segundos';
    if (absMin < 60) return past ? `há ${absMin} min` : `em ${absMin} min`;
    const absH = Math.round(absMin / 60);
    if (absH < 24) return past ? `há ${absH}h` : `em ${absH}h`;
    const absD = Math.round(absH / 24);
    if (absD < 7) return past ? `há ${absD} dia${absD > 1 ? 's' : ''}` : `em ${absD} dia${absD > 1 ? 's' : ''}`;
    // Mais que uma semana: mostra data completa
    return fmtDateTime(d);
}
